"use client";
import { useState } from "react";
import { useCheckoutForm } from "./form";
import { useDispatch } from "react-redux";
import {
  clearCoupon as clearGlobalCoupon,
  setCoupon as setGlobalCoupon,
} from "@/redux/features/coupon-slice";
import toast from "react-hot-toast";
import { useTranslations } from "next-intl";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { useAppSelector } from "@/redux/store";
import { nexusApi } from "@/lib/nexus-api";
import { DiscountType } from "@/types/discount";
import { XIcon } from "@/assets/icons";

export default function CouponForm() {
  const { setValue, watch } = useCheckoutForm();
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState("");
  const dispatch = useDispatch();
  const t = useTranslations("CartDiscount");
  const ecommerce = useEcommerce();
  const { clientProfile } = useAppSelector((state) => state.authReducer);
  const appliedCouponCode = (watch("couponCode") ?? "").trim();
  const alreadyApplied = Boolean(appliedCouponCode);

  const applyCoupon = async () => {
    setLoading(true);
    if (!coupon.trim()) {
      setLoading(false);
      return;
    }

    try {
      const res = await nexusApi.post(
        `/public/${ecommerce.id}/discount-codes/validate`,
        {
          code: coupon.trim(),
          clientEmail: clientProfile?.email ?? undefined,
        }
      );

      const responseData = res.data;

      if (responseData.success && responseData.data) {
        const { code, discountValue, discountType } = responseData.data;

        setValue("couponCode", code);
        setValue("couponDiscountValue", discountValue);
        setValue("couponDiscountType", discountType as DiscountType);

        dispatch(
          setGlobalCoupon({
            couponCode: code,
            couponDiscountValue: discountValue,
            couponDiscountType: discountType as DiscountType,
          })
        );

        toast.success(t("successCoupon"));
        setCoupon("");
      } else {
        toast.error(t("failedCoupon"));
      }
    } catch (err) {
      toast.error(t("failedCoupon"));
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    setValue("couponCode", "");
    setValue("couponDiscountValue", 0);
    setValue("couponDiscountType", undefined);
    setCoupon("");
    dispatch(clearGlobalCoupon());
    toast.success(t("couponRemoved"));
  };

  return (
    <>
      {alreadyApplied ? (
        <div className="flex w-full items-center gap-3">
          <div className="flex h-[50px] flex-1 items-center rounded-full border border-gray-3 bg-gray-1 px-5">
            <span className="truncate font-medium text-dark">
              {appliedCouponCode}
            </span>
          </div>

          <button
            type="button"
            onClick={removeCoupon}
            className="group relative inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-red/8 text-red ease-out duration-200 hover:bg-red hover:text-white hover:shadow-lg hover:shadow-red/25 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-red/30 active:translate-y-0 active:shadow-sm"
            aria-label={t("removeCouponAria")}
            title={t("removeCouponAria")}
          >
            <XIcon className="h-[16px] w-[16px] transition-all duration-200 group-hover:scale-110 group-hover:rotate-90" />
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder={t("placeholder")}
            className="rounded-full border border-gray-3 bg-gray-1 placeholder:text-dark-5 w-full py-2.5 px-5 outline-hidden duration-200 focus:border-transparent focus:shadow-input focus:ring-2 focus:ring-blue/20 disabled:opacity-80"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
          />

          <button
            type="button"
            onClick={applyCoupon}
            className="inline-flex disabled:pointer-events-none disabled:opacity-80 font-medium text-white bg-blue py-3 px-6 rounded-full ease-out duration-200 hover:bg-blue-dark"
            disabled={loading}
          >
            {loading ? t("applying") : t("apply")}
          </button>
        </>
      )}
    </>
  );
}
